/*
 * "FIND ACTIVITY", WITHOUT THE PANEL AROUND IT.
 *
 * The Simulate panel's search has more moving parts than its markup: a
 * selection that clears the list when it changes, a span, a search in flight
 * that a Stop, a new document or a teardown must end, and one sandbox kept
 * across searches and replaced when the watchdog kills it. This holds all of
 * that as a Lit reactive controller, so the panel only renders it — and a Node
 * test can drive it against the real engine, with no DOM.
 *
 * The search's modules load on first use (`load`): the model page is in the
 * entry chunk, and most visits to it never search.
 */
import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { GroupSpec } from '@vitesse/statechart-host';
import type { HistorySource, ReplayEngine } from '../../../sandbox/statechart/replay';
import type { ScanResult, scan } from '../../../sandbox/statechart/scan';
import { EMPTY_SELECTION, onlyUnreachableSelected, type ActivitySelection } from './activity-filter';
import type { StateCoverage } from './replay-coverage';

/** A sandbox a search runs in. `dead` once its watchdog has killed it. */
export interface SearchSandbox extends ReplayEngine {
  readonly dead: boolean;
}

/** What a search needs that the panel only has once it has been loaded. */
export interface SearchRuntime {
  scan: typeof scan;
  newSandbox(): SearchSandbox;
}

export interface SearchRequest {
  scxml: string;
  /** The model as a group (children, or a root with its own wiring), as the replay takes it. */
  group?: GroupSpec;
  history: HistorySource;
  /** The document's own coverage check, when it could be made. */
  coverage: readonly StateCoverage[] | null;
  /** The sentence for a search refused because every chosen state is unreachable. */
  allUnreachable: string;
}

export class ActivitySearch implements ReactiveController {
  /** Which periods to keep: what the chips say. Empty is the unfiltered search. */
  selection: ActivitySelection = EMPTY_SELECTION;
  spanMs: number;
  finding = false;
  /** Fraction of the span covered, 0–1. */
  progress = 0;
  /** What the historian said the search will read, once it has said. */
  expected: number | null = null;
  found: ScanResult | null = null;
  /** The search's own sentence when it declined to run. */
  refusal = '';
  /** Something broke that is not a refusal. */
  failure = '';
  /** The list row whose window is being replayed. */
  chosenEpisode = -1;

  private abort: AbortController | null = null;
  /** Bumped by every clear: a search started before it answers a question
   *  nobody is asking any more, and its result is dropped. A Stop does not
   *  bump it — a stopped search's partial list is what Stop is for. */
  private generation = 0;
  /** Kept between searches: the frame and the worker, with the engine inside
   *  it, come up once, and each search boots afresh inside it. */
  private sandbox: SearchSandbox | null = null;

  constructor(
    private readonly host: ReactiveControllerHost,
    private readonly load: () => Promise<SearchRuntime>,
    spanMs: number,
  ) {
    this.spanMs = spanMs;
    host.addController(this);
  }

  hostDisconnected(): void {
    this.stop();
    this.generation++;
    this.sandbox?.dispose();
    this.sandbox = null;
  }

  /** Ends a search in flight between slices; what it found so far is kept. */
  stop(): void {
    this.abort?.abort();
  }

  /** The list on screen describes a question that has changed: drop it, and
   *  stop any search still answering the old one. */
  clear(): void {
    this.stop();
    this.generation++;
    this.found = null;
    this.refusal = '';
    this.failure = '';
    this.chosenEpisode = -1;
    this.host.requestUpdate();
  }

  /** Done in the click, not on the next update: "Show all" changes the
   *  selection and starts a search in the same breath, and a clear that ran
   *  later would stop the search it had just started. */
  setSelection(next: ActivitySelection): void {
    this.selection = next;
    this.clear();
  }

  setSpan(spanMs: number): void {
    this.spanMs = spanMs;
    this.clear();
  }

  /** A different document has different states, so chips chosen for the old one mean nothing. */
  resetForDocument(): void {
    this.setSelection(EMPTY_SELECTION);
  }

  choose(index: number): void {
    this.chosenEpisode = index;
    this.host.requestUpdate();
  }

  /*
   * WHERE IN THE HISTORY DOES IT ACT? Runs the model over the chosen span,
   * keeping only the stretches where it changed state, would have written, or
   * hit an error. Every exit — a refusal, a failure, a module that would not
   * load, a sandbox the watchdog killed — ends with `finding` false again.
   *
   * The request can be a function that makes it, or that gives a refusal: the
   * panel reads a group from the relay to make it. The search is busy from the
   * call on, so a second press starts nothing, and a Stop, a clear or a
   * teardown during that read ends it.
   */
  async find(request: SearchRequest | (() => Promise<SearchRequest | string>)): Promise<void> {
    if (this.finding) return;
    this.found = null;
    this.refusal = '';
    this.failure = '';
    this.chosenEpisode = -1;
    this.finding = true;
    this.progress = 0;
    this.expected = null;
    this.host.requestUpdate();
    const ac = new AbortController();
    this.abort = ac;
    const gen = this.generation;
    let sandbox: SearchSandbox | null = null;
    try {
      const req = typeof request === 'function' ? await request() : request;
      // Cleared, stopped or torn down while the request was made: it answers nothing now.
      if (gen !== this.generation || ac.signal.aborted) return;
      if (typeof req === 'string') {
        this.refusal = req;
        return;
      }
      // Every chosen state is one the document itself says a replay can never
      // reach: the answer is known, and a search would only confirm it slowly.
      if (req.coverage && onlyUnreachableSelected(this.selection, req.coverage)) {
        this.refusal = req.allUnreachable;
        return;
      }
      const rt = await this.load();
      // Cleared or torn down while the modules loaded: never open a sandbox for it.
      if (gen !== this.generation) return;
      if (this.sandbox?.dead) {
        this.sandbox.dispose();
        this.sandbox = null;
      }
      sandbox = this.sandbox ?? rt.newSandbox();
      this.sandbox = sandbox;
      const now = Date.now();
      const out = await rt.scan(
        {
          scxml: req.scxml,
          ...(req.group ? { group: req.group } : {}),
          t0Ms: now - this.spanMs,
          t1Ms: now,
          filter: this.selection,
          signal: ac.signal,
          onPlanned: (p) => {
            this.expected = p.rows;
            this.host.requestUpdate();
          },
          onProgress: (f) => {
            this.progress = f;
            this.host.requestUpdate();
          },
        },
        sandbox,
        req.history,
      );
      if (gen !== this.generation) return;
      if (out.ok) this.found = out;
      else this.refusal = out.refusal;
    } catch (e) {
      if (gen === this.generation) this.failure = e instanceof Error ? e.message : String(e);
    } finally {
      // A kill leaves the frame in the document; only dispose takes it out.
      if (sandbox?.dead) {
        sandbox.dispose();
        if (this.sandbox === sandbox) this.sandbox = null;
      }
      if (this.abort === ac) this.abort = null;
      this.finding = false;
      this.host.requestUpdate();
    }
  }
}
